import { useCallback, useState, useEffect, useRef } from 'react'
import PopperJS from 'popper.js'
import { space } from '../style';

const defaultOpts = {
  placement: 'bottom-start',
  eventsEnabled: true,
  positionFixed: false,
  modifiers:{
    preventOverflow: {
      boundariesElement: 'viewport',
      padding: space.large
    }
  }
}

const initialStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  opacity: 0,
  pointerEvents: 'none'
}

const noop = () => {}

export default function usePopper(inputOpts, inputs = []) {
  const opts = { ...defaultOpts, ...inputOpts }
  opts.modifiers = { ...defaultOpts.modifiers, ...inputOpts.modifiers }
  const { referenceRef, popperRef } = opts
  const [state, setState] = useState({
    data: undefined,
    placement: undefined
  })
  const popperInstance = useRef()

  const callbackFn = useCallback(data => {
    const { placement } = data
    setState({ data, placement })
    return data
  }, [])

  useEffect(() => {
    // A placement difference in state means popper determined a new placement
    // apart from the opts value. By the time the popper element is rendered with
    // the new position Popper has already measured it, if the place change triggers
    // a size change it will result in a misaligned popper. So we schedule an update to be sure.
    if (popperInstance.current) {
      popperInstance.current.scheduleUpdate()
    }
  }, [state.placement])

  useEffect(() => {
    if (referenceRef.current && popperRef.current) {
      popperInstance.current = new PopperJS(
        referenceRef.current,
        popperRef.current,
        {
          placement: opts.placement,
          eventsEnabled: opts.eventsEnabled,
          positionFixed: opts.positionFixed,
          modifiers: {
            ...opts.modifiers,
            applyStyle: { enabled: false },
            updateStateModifier: {
              enabled: true,
              order: 900,
              fn: callbackFn
            }
          }
        }
      )
    }

    return () => {
      if (popperInstance.current) {
        popperInstance.current.destroy()
        popperInstance.current = null
      }
    }
  }, inputs)

  const style =
    !popperRef.current || !state.data
      ? initialStyle
      : {
          position: state.data.offsets.popper.position,
          ...state.data.styles
        }

  return {
    style,
    placement: state.placement,
    outOfBoundaries: state.data && state.data.hide,
    scheduleUpdate: popperInstance ? popperInstance.scheduleUpdate : noop,
  }
}
